<aside
  class="sidebar"
  [class.sidebarCollapsed]="collapsed() && !isMobile()"
  [class.mobileSidebar]="isMobile()"
  [class.open]="isMobileOpen()"
  role="complementary"
  aria-label="Main sidebar"
>
  @if (!isMobile()) {
    <button
      type="button"
      class="sidebarToggle"
      [class.collapsed]="collapsed()"
      (click)="toggleCollapse.emit()"
      [attr.aria-label]="getToggleLabel()"
      [attr.aria-expanded]="(!collapsed()).toString()"
      [attr.aria-controls]="navId"
    >
      <span class="sidebarToggleIcon">‹</span>
    </button>
  }

  <div class="sidebarBrand">
    <img [src]="brand.logoPath" [alt]="brand.logoAlt" class="sidebarLogo" />
  </div>

  <nav class="sidebarNav" [id]="navId" aria-label="Primary navigation">
    @for (item of items(); track item.key) {
      @if (isLogoutItem(item)) {
        <button
          type="button"
          class="navButton"
          [attr.title]="item.label"
          [attr.aria-label]="item.label"
          [attr.data-tooltip]="collapsed() || isMobile() ? item.label : null"
          (click)="onLogout()"
        >
          <span class="navIcon">
            @switch (item.icon) {
              @case ('logout') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M15 16l5-4-5-4M20 12H9"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
            }
          </span>
          @if (!(collapsed() && !isMobile())) {
            <span class="navLabel">{{ item.label }}</span>
          }
        </button>
      } @else {
        <a
          #rla="routerLinkActive"
          class="navButton"
          [class.navButton-movements]="item.key === 'movements'"
          [routerLink]="item.route"
          routerLinkActive="active"
          [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
          [attr.title]="item.label"
          [attr.aria-label]="item.label"
          [attr.aria-current]="rla.isActive ? 'page' : null"
          [attr.data-tooltip]="collapsed() || isMobile() ? item.label : null"
          (click)="closeMobile.emit()"
        >
          <span class="navIcon">
            @switch (item.icon) {
              @case ('dashboard') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z" fill="currentColor" />
                </svg>
              }
              @case ('products') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5zM12 5.2 6.1 8.5 12 11.8l5.9-3.3zM6 10.2v5.6l5 2.8V13zM18 10.2 13 13v5.6l5-2.8z"
                    fill="currentColor"
                  />
                </svg>
              }
              @case ('inventory') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 4h18v4H3zm1 6h7v10H4zm9 0h7v4h-7zm0 6h7v4h-7z" fill="currentColor" />
                </svg>
              }
              @case ('movements') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M4 7h12m0 0-3-3m3 3-3 3"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M20 17H8m0 0 3-3m-3 3 3 3"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              @case ('reports') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M4 4h16v16H4zm3 12h2V9H7zm4 0h2V7h-2zm4 0h2v-4h-2z" fill="currentColor" />
                </svg>
              }
              @case ('development') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="m8 6-6 6 6 6m8-12 6 6-6 6M14 3l-4 18" fill="none" stroke="currentColor" stroke-width="2" />
                </svg>
              }
              @case ('settings') {
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M19.14 12.94a7.43 7.43 0 0 0 .05-.94 7.43 7.43 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.18 7.18 0 0 0-1.62-.94l-.36-2.54A.5.5 0 0 0 13.9 2h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.13.54-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.72 8.48a.5.5 0 0 0 .12.64l2.03 1.58c-.03.31-.05.62-.05.94s.02.63.05.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.49.4 1.04.71 1.62.94l.36 2.54a.5.5 0 0 0 .49.42h3.8a.5.5 0 0 0 .49-.42l.36-2.54c.58-.23 1.13-.54 1.62-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64zm-7.14 2.56a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z"
                    fill="currentColor"
                  />
                </svg>
              }
            }
          </span>
          @if (!(collapsed() && !isMobile())) {
            <span class="navLabel">{{ item.label }}</span>
          }
        </a>
      }
    }
  </nav>

  <div class="sidebarFooter">
    @if (!(collapsed() && !isMobile())) {
      <app-language-switcher tone="sidebar" />
    }
  </div>
</aside>
